import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Switch,
  Select,
  MenuItem,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  Chip,
  Button,
  IconButton,
  Grid,
  Avatar,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AppsIcon from "@mui/icons-material/Apps";
import AssessmentIcon from "@mui/icons-material/Assessment";
import BusinessIcon from "@mui/icons-material/Business";
import CampaignIcon from "@mui/icons-material/Campaign";
import SalesPromotionsDialog from "../SalesPromotionsDialog";

const ACCENT = "#1976d2";
const SWITCH_PAGES_EXCLUDED = ["ViewObligoData", "ResearchMonitorRole", "ObligoExceptionPage"];

const SectionHeader = ({ icon: Icon, title }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
    <Box
      sx={{
        width: 30,
        height: 30,
        borderRadius: "50%",
        bgcolor: alpha(ACCENT, 0.12),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon sx={{ color: ACCENT, fontSize: 17 }} />
    </Box>
    <Typography variant="subtitle2" fontWeight={700}>
      {title}
    </Typography>
  </Box>
);

const PermissionToggle = ({ label, checked, onChange, disabled }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1,
      px: 1.5,
      py: 0.75,
      borderRadius: 2.5,
      border: `1px solid ${checked ? alpha(ACCENT, 0.3) : "rgba(0,0,0,0.08)"}`,
      bgcolor: checked ? alpha(ACCENT, 0.06) : "transparent",
      transition: "all 0.15s ease",
    }}
  >
    <Typography variant="body2" sx={{ fontWeight: checked ? 600 : 400, color: checked ? "#1e40af" : "text.primary" }} noWrap>
      {label}
    </Typography>
    <Switch checked={checked} onChange={onChange} disabled={disabled} size="small" />
  </Box>
);

const EditRolePermissionsDialog = ({
  role,
  open,
  onClose,
  pageNames,
  translation,
  handleCheckboxChange,
  disabledCheckbox,
  updateRole,
  allBranches,
  translateKey,
  getDisplayValue,
  mockDropdownData,
  setSnackbar,
  snackbar,
  rolesList,
  setRolesList,
  execute,
}) => {
  if (!role) return null;

  const switchPages = pageNames.filter((p) => !SWITCH_PAGES_EXCLUDED.includes(p));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: "hidden" } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          py: 2,
          background: `linear-gradient(135deg, ${ACCENT} 0%, ${alpha(ACCENT, 0.75)} 100%)`,
          color: "#fff",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Avatar sx={{ bgcolor: "rgba(255,255,255,0.18)", width: 38, height: 38, fontWeight: 700 }}>
            {role.Name?.charAt(0) || "?"}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              הרשאות תפקיד
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {role.Name}
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: "#fff" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ bgcolor: "#fafafa", p: 3 }}>
        <Box sx={{ bgcolor: "#fff", borderRadius: 3, p: 2.5, mb: 2.5, border: "1px solid rgba(0,0,0,0.06)" }}>
          <SectionHeader icon={AppsIcon} title="גישה לעמודים" />
          <Grid container spacing={1.25}>
            {switchPages.map((page) => (
              <Grid item xs={12} sm={6} md={4} key={page}>
                <PermissionToggle
                  label={translation[page] || page}
                  checked={!!role?.[page]}
                  onChange={handleCheckboxChange(role?.Name, page)}
                  disabled={disabledCheckbox(page)}
                />
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box sx={{ bgcolor: "#fff", borderRadius: 3, p: 2.5, mb: 2.5, border: "1px solid rgba(0,0,0,0.06)" }}>
          <SectionHeader icon={AssessmentIcon} title="הרשאות מיוחדות" />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                {translation.obligoException}
              </Typography>
              <Select
                fullWidth
                size="small"
                value={role?.ObligoExceptionPage ? "full" : role?.ViewObligoData ? "readonly" : "none"}
                onChange={handleCheckboxChange(role?.Name, "ObligoExceptionPage")}
                disabled={disabledCheckbox("ObligoExceptionPage")}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="none">{translation.obligoAccessNone}</MenuItem>
                <MenuItem value="readonly">{translation.obligoAccessReadonly}</MenuItem>
                <MenuItem value="full">{translation.obligoAccessFull}</MenuItem>
              </Select>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                {translation.researchMonitorRole}
              </Typography>
              <Select
                fullWidth
                size="small"
                value={role?.ResearchMonitorRole || "none"}
                onChange={handleCheckboxChange(role?.Name, "ResearchMonitorRole")}
                disabled={disabledCheckbox("ResearchMonitorRole")}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="none">אין גישה</MenuItem>
                <MenuItem value="employee">עובד</MenuItem>
                <MenuItem value="manager">מנהל</MenuItem>
              </Select>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ bgcolor: "#fff", borderRadius: 3, p: 2.5, mb: 2.5, border: "1px solid rgba(0,0,0,0.06)" }}>
          <SectionHeader icon={BusinessIcon} title="סניפים" />
          <Select
            name="Branches"
            multiple
            value={role?.Branches || []}
            onChange={(e) => updateRole(e, role)}
            fullWidth
            size="small"
            sx={{ borderRadius: 2 }}
            renderValue={(selected) => {
              const selectedBranchesDetails = selected.map(
                (selectedBranch) =>
                  allBranches.length &&
                  allBranches.find((branch) => branch?.BRANCHNAME === selectedBranch.toString()),
              );
              if (selectedBranchesDetails.length <= 3) {
                return selectedBranchesDetails
                  .map((branch) => `${branch?.BRANCHNAME} - ${branch?.BRANCHDES}`)
                  .join(", ");
              }
              return `${selectedBranchesDetails
                .slice(0, 3)
                .map((branch) => `${branch?.BRANCHNAME} - ${branch?.BRANCHDES}`)
                .join(", ")} + ${selectedBranchesDetails.length - 3} נוספים`;
            }}
          >
            {allBranches.length &&
              allBranches?.map((branch, index) => (
                <MenuItem key={index} value={branch.BRANCHNAME}>
                  {branch.BRANCHNAME} - {branch.BRANCHDES}
                </MenuItem>
              ))}
          </Select>
          <FormHelperText>{translation.selectMultipleBranches}</FormHelperText>
        </Box>

        {role?.SalesPromotionsPage && (
          <Box sx={{ bgcolor: "#fff", borderRadius: 3, p: 2.5, border: "1px solid rgba(0,0,0,0.06)" }}>
            <SectionHeader icon={CampaignIcon} title={role?.properties[0]?.SalesPromotions?.name || "קד״מ ודוגמיות"} />
            <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={role?.properties[0]?.SalesPromotions?.OrderCreator || false}
                    name="OrderCreator"
                    onChange={(e) => updateRole(e, role)}
                    size="small"
                  />
                }
                label={translation.orderCreator}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={role?.properties[0]?.SalesPromotions?.OrderApprover || false}
                    name="OrderApprover"
                    onChange={(e) => updateRole(e, role)}
                    size="small"
                  />
                }
                label={translation.orderApprover}
              />
            </Box>
            {role?.properties[0]?.SalesPromotions?.OrderCreator && (
              <Box sx={{ p: 2, border: "1px solid #ddd", borderRadius: 2 }}>
                {role?.properties?.length > 0 ? (
                  role?.properties?.map((property, index) => {
                    const propertyKey = Object.keys(property)[0];
                    const propertyData = property[propertyKey];
                    return (
                      <Box key={index} sx={{ mb: 2 }}>
                        {Object.entries(propertyData).map(([key, value], i) => {
                          if (key === "name" || key === "OrderCreator" || key === "OrderApprover") return null;
                          return (
                            <Typography key={i} component="div" sx={{ mb: 0.5 }}>
                              <strong>{translateKey(key)}:</strong>{" "}
                              {key === "family" && Array.isArray(value) ? (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                                  {value.map((f, fi) => {
                                    const full = mockDropdownData.family?.find((item) => item._id === f._id);
                                    const label = full ? `${full.familyName} - ${full.familyDes}` : f.des;
                                    return <Chip key={fi} label={label} size="small" />;
                                  })}
                                </Box>
                              ) : (
                                getDisplayValue(key, value)
                              )}
                            </Typography>
                          );
                        })}
                      </Box>
                    );
                  })
                ) : (
                  <Typography>{translation.noData}</Typography>
                )}
                <SalesPromotionsDialog
                  mockDropdownData={mockDropdownData}
                  setSnackbar={setSnackbar}
                  rolesList={rolesList}
                  setRolesList={setRolesList}
                  execute={execute}
                  role={role}
                />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: "#fafafa" }}>
        <Button onClick={onClose} variant="contained" sx={{ borderRadius: 2.5, textTransform: "none" }}>
          סגירה
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditRolePermissionsDialog;
